import { AudioOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useKeyboardNavigation from '../../../hooks/useKeyboardNavigation';
import AudioFeedback from '../AudioFeedback/AudioFeedback';
import './VoiceRecorder.css';

const { Title, Text } = Typography;

// Маппинг языков для Web Speech API
const SPEECH_LANG_MAP = {
  'ru': 'ru-RU',
  'ky': 'ky-KG', 
  'en': 'en-US'
};

const VoiceRecorder = ({ 
  onTranscriptReceived, 
  isLoading = false, 
  placeholder = "Нажмите кнопку и говорите...",
  ariaLabel = "Голосовой ввод",
  ariaDescribedBy = null
}) => {
  const { i18n, t } = useTranslation('global');
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(true);
  
  const recognitionRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  useEffect(() => {

    // Проверяем поддержку Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
      setError('Ваш браузер не поддерживает голосовой ввод. Используйте Chrome, Edge или Safari.');
    }
    
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    if (!isSupported) return;
    
    try {
      setError(null);
      setTranscript('');
      setInterimTranscript('');
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      // Получаем язык распознавания речи на основе текущего языка интерфейса
      const currentLang = i18n.language || 'ru';
      const speechLang = SPEECH_LANG_MAP[currentLang] || 'ru-RU';
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = speechLang; // Используем текущий язык
      recognition.maxAlternatives = 1;
      

      let finalTranscriptText = '';

      recognition.onresult = (event) => {
        let interimText = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptPiece = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            finalTranscriptText += transcriptPiece + ' ';
            setTranscript(finalTranscriptText);
          } else {
            interimText += transcriptPiece;
          }
        }
        
        setInterimTranscript(interimText);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        if (event.error === 'no-speech') {
          setError('Речь не обнаружена. Попробуйте говорить громче.');
        } else if (event.error === 'audio-capture') {
          setError('Не удалось получить доступ к микрофону.');
        } else if (event.error === 'not-allowed') {
          setError('Доступ к микрофону запрещен. Разрешите доступ в настройках браузера.');
        } else {
          setError(`Ошибка распознавания: ${event.error}`);
        }
        
        setIsRecording(false);
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
        }
      };

      recognition.onend = () => {
        // Если запись еще идет, перезапускаем (для continuous режима)
        if (isRecording && recognitionRef.current) {
          try {
            recognition.start();
          } catch (e) {
          }
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
      
      setIsRecording(true);
      setRecordingTime(0);
      
      // Таймер записи
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Не удалось начать запись. Проверьте разрешения микрофона.');
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    
    setIsRecording(false);
    
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }
  };

  const sendTranscript = () => {
    const fullText = transcript.trim();
    
    if (!fullText) {
      setError('Транскрипт пуст. Запишите голосовое сообщение.');
      return;
    }
    
    onTranscriptReceived(fullText);
  };

  const resetRecording = () => {
    setTranscript('');
    setInterimTranscript('');
    setRecordingTime(0);
    setError(null);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Клавиатурная навигация
  const { keyboardInstructions } = useKeyboardNavigation({
    onStartRecording: startRecording,
    onStopRecording: stopRecording,
    onSendTranscript: sendTranscript,
    onResetRecording: resetRecording,
    isRecording,
    hasTranscript: !!transcript,
    isLoading
  });

  return (
    <Card className="voice-recorder" title={t('voice.realtimeInput')}>
      <AudioFeedback 
        isRecording={isRecording}
        transcript={transcript}
        error={error}
        success={transcript && !isRecording}
      />
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        
        {error && (
          <Alert
            message={t('voice.error')}
            description={error}
            type="error"
            closable
            onClose={() => setError(null)}
          />
        )}

        <div className="recording-controls">
          {!isRecording && !transcript && (
            <Button
              type="primary"
              size="large"
              icon={<AudioOutlined />}
              onClick={startRecording}
              disabled={isLoading || !isSupported}
            >
              {t('voice.startRecording')}
            </Button>
          )}

          {isRecording && (
            <div className="recording-active">
              <div className="recording-indicator">
                <div className="pulse-dot" />
                <Text strong>{t('voice.recording')} {formatTime(recordingTime)}</Text>
              </div>
              
              {/* Показываем текст в реальном времени */}
              <div className="live-transcript">
                <Text type="secondary">{t('voice.recognizedText')}</Text>
                <div className="transcript-box">
                  <Text>{transcript}</Text>
                  <Text type="secondary" italic>{interimTranscript}</Text>
                </div>
              </div>
              
              <Button
                type="default"
                danger
                size="large"
                icon={<StopOutlined />}
                onClick={stopRecording}
              >
                {t('voice.stopRecording')}
              </Button>
            </div>
          )}

          {transcript && !isRecording && (
            <div className="recording-complete">
              <div className="transcript-result">
                <Text strong>{t('voice.recognizedText')}</Text>
                <div className="transcript-box">
                  <Text>{transcript}</Text>
                </div>
              </div>
              
              <Space>
                <Button
                  type="primary"
                  size="large"
                  icon={<SendOutlined />}
                  onClick={sendTranscript}
                  disabled={isLoading}
                >
                  {t('voice.createFromText')}
                </Button>
                <Button onClick={resetRecording}>
                  {t('voice.recordAgain')}
                </Button>
              </Space>
            </div>
          )}
        </div>

        <div 
          className="voice-tips"
          role="complementary"
          aria-label="Советы для улучшения качества распознавания голоса"
        >
          <Title level={5}>{t('voice.tipsTitle')}</Title>
          <ul>
            <li>{t('voice.tip1')}</li>
            <li>{t('voice.tip2')}</li>
            <li>{t('voice.tip3')}</li>
            <li>{t('voice.tip4')}</li>
            <li>{t('voice.tip5')}</li>
          </ul>
        </div>

        <div 
          className="keyboard-instructions"
          role="complementary"
          aria-label="Инструкции по клавиатурной навигации"
        >
          <Title level={5}>{t('voice.keyboardControls')}</Title>
          <ul>
            <li>{t('voice.spaceKey')}</li>
            <li>{t('voice.enterKey')}</li>
            <li>{t('voice.escapeKey')}</li>
            <li>{t('voice.rKey')}</li>
          </ul>
        </div>
      </Space>
    </Card>
  );
};

export default VoiceRecorder;